import type { ReactNode } from 'react';
import { Icon, type IconName } from '../shell/Icon.tsx';
import styles from './Toolbar.module.css';

/* ==========================================================================
   Toolbar + SegmentedControl.

   Filters and view switches live in a 30px row above the data, never in a
   form grid that costs 180px before a single datum appears.
   ========================================================================== */

export function Toolbar({
  flush = false,
  label,
  children,
}: {
  flush?: boolean | undefined;
  label?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className={styles.toolbar} data-flush={flush} role="toolbar" aria-label={label}>
      {children}
    </div>
  );
}

/** Pushes everything after it to the right edge. */
export function ToolbarSpacer() {
  return <span className={styles.spacer} />;
}

/** A vertical hairline between groups of controls. */
export function ToolbarDivider() {
  return <span className={styles.divider} aria-hidden="true" />;
}

/** `Filter by:` / `Sort by:` — an uppercase micro-label inside the bar. */
export function ToolbarLabel({ children }: { children: ReactNode }) {
  return <span className={styles.label}>{children}</span>;
}

export interface SegmentItem<V extends string> {
  value: V;
  label: string;
  /** `number | string`, so a facet count can be `—` when its source is
      unread. `Fault 0` off a dead roster is a census nobody took. */
  count?: number | string | undefined;
  icon?: IconName | undefined;
  disabled?: boolean | undefined;
}

/**
 * Exclusive choice. Only the active segment is filled — an all-filled strip
 * reads as five buttons rather than one control with a current value.
 */
export function SegmentedControl<V extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: readonly SegmentItem<V>[];
  value: V;
  onChange: (next: V) => void;
  label: string;
}) {
  return (
    <div className={styles.segment} role="radiogroup" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="radio"
          aria-checked={it.value === value}
          disabled={it.disabled}
          className={styles.seg}
          onClick={() => onChange(it.value)}
        >
          {it.icon !== undefined && <Icon name={it.icon} size={14} />}
          {it.label}
          {it.count !== undefined && <span className={styles.segCount}>{it.count}</span>}
        </button>
      ))}
    </div>
  );
}
